import { useLayoutEffect, useState, type RefObject } from 'react';
import { canFitCo2LabelIcon } from './co2-label-layout.helpers';

/**
 * Hides the cloud on every bar in one stage when any numeric label fails
 * {@link canFitCo2LabelIcon}. Measuring only the numeric span keeps the
 * decision stable after the icons disappear.
 *
 * `versionId` is required because callers key the stage element by it. The
 * ref object stays stable across that remount, so the id is what binds
 * observers to the replacement node.
 */
export const useSharedCo2LabelIconVisibility = (
  stageRef: RefObject<HTMLDivElement | null>,
  versionId: string,
): boolean => {
  const [visible, setVisible] = useState(true);

  // versionId is not read in the effect. The stage element is keyed by it
  // while stageRef stays the same object, so this dependency disconnects
  // observers from the detached node. A detached stage reports width 0 and
  // hides the cloud until reload.
  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const update = () => {
      const values = Array.from(
        stage.querySelectorAll<HTMLElement>('[data-co2-label-value]'),
      );
      const nextVisible = values.every((value) => {
        const bar = value.closest<HTMLElement>('[data-co2e-bar]');
        return (
          bar !== null &&
          canFitCo2LabelIcon(
            bar.getBoundingClientRect().width,
            value.getBoundingClientRect().width,
          )
        );
      });
      setVisible((current) =>
        current === nextVisible ? current : nextVisible,
      );
    };

    const resizeObserver = new ResizeObserver(update);
    // Rebind when bars mount. Width changes, including label text, come through
    // the resize observer. A dep-less effect forced layout on every render.
    const observe = () => {
      resizeObserver.disconnect();
      resizeObserver.observe(stage);
      stage
        .querySelectorAll<HTMLElement>('[data-co2-label-value]')
        .forEach((value) => resizeObserver.observe(value));
      update();
    };
    observe();
    const mutationObserver = new MutationObserver(observe);
    mutationObserver.observe(stage, {
      childList: true,
      subtree: true,
    });
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [stageRef, versionId]);

  return visible;
};
